import { useEffect, useMemo, useRef, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getLang from 'I18n/utils/getLang';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import PRODUCT_TIER_ORDERS from 'self-service-api/constants/ProductTierOrders';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'self... Remove this comment to see the full error message
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getIsFreePortal, hasActiveProTrial } from 'onboarding-scopes/monetization-utils';
import { fetchHubCreationDate } from '../api/fetchHub';
import { fetchTrialBannerStatus } from '../api/fetchTrialBannerStatus';
import ProductName from '../components/ProductName';
import * as TRIAL_STATUS from '../constants/trialStatus';
import { getHighestProductOwnedTier } from './productUtils';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Has the user ever activated a given trial?
 * @param {*} apiName
 * @param {*} trialState
 * @returns
 */
export const hasHadTrial = (apiName, trialState) => {
  return !!(trialState[apiName] && (trialState[apiName].hasTrialed || trialState[apiName].inTrial));
};

/**
 * Is the given trial active or expired (or neither)?
 * @param {*} upgradeProduct
 * @param {*} trialState
 * @returns boolean
 */
export const getTrialStatus = (upgradeProduct, trialState) => {
  const apiName = UpgradeProductToApiNameMap[upgradeProduct];
  const trial = trialState[apiName];
  if (!trial) {
    return TRIAL_STATUS.TRIAL_NOT_ACTIVE;
  }
  if (trial.inTrial) {
    return TRIAL_STATUS.TRIAL_ACTIVE;
  } else if (trial.hasTrialed) {
    return TRIAL_STATUS.TRIAL_EXPIRED;
  } else {
    return TRIAL_STATUS.TRIAL_NOT_ACTIVE;
  }
};

/**
 * Given an upgradeProduct, return the highest tier of that trial that has been activated;
 * @param {*} upgradeProduct
 * @param {*} trialState
 * @returns an upgrade product
 */
export const getHighestTrialTier = (upgradeProduct, trialState) => {
  const apiName = UpgradeProductToApiNameMap[upgradeProduct];
  // An array of tiered apiNames for the hub your current apiName is in
  const tieredProductsGroup = PRODUCT_TIER_ORDERS.filter(productTierOrder => productTierOrder.indexOf(apiName) !== -1)[0];
  if (!tieredProductsGroup) {
    return null;
  }

  // Index of where in the tier this apiName is
  const tierIndex = tieredProductsGroup.indexOf(apiName);
  // Group of all products higher than this tier, reversed so we can use find()
  const higherTieredProductGroups = tieredProductsGroup.slice(tierIndex, tieredProductsGroup.length).reverse();
  const higherTieredProduct = higherTieredProductGroups.find(tieredApiName => {
    return hasHadTrial(tieredApiName, trialState);
  });
  return higherTieredProduct ? ApiNameToUpgradeProductMap[higherTieredProduct] : null;
};

/**
 * Get the reason that a particular trial can't be activated
 * @param {*} upgradeProduct upgradeProduct to compare
 * @param {*} trialState response from getTrialState api
 * @param {*} ownedProducts current owned products
 * @returns
 */
export const getTrialAvailabilityReason = (upgradeProduct, trialState, ownedProducts) => {
  const apiName = UpgradeProductToApiNameMap[upgradeProduct];

  /// Has this account activated either this trial or a higher tier?
  const highestTrialTier = getHighestTrialTier(upgradeProduct, trialState);
  /// what is the status of that trial?
  const highestTrialStatus = getTrialStatus(highestTrialTier, trialState);

  /// what is the highest tier of this product the account owns?
  const ownedProductTier = getHighestProductOwnedTier(upgradeProduct, ownedProducts);
  if (highestTrialTier && highestTrialStatus === TRIAL_STATUS.TRIAL_ACTIVE) {
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "trial-components-library.picker.tooltips.trialActive",
      "data-test-id": 'picker-tooltip-trialActive',
      options: {
        productName: /*#__PURE__*/_jsx(ProductName, {
          upgradeProduct: highestTrialTier
        })
      }
    });
  }
  if (ownedProductTier) {
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "trial-components-library.picker.tooltips.productOwned",
      "data-test-id": 'picker-tooltip-productOwned',
      options: {
        productName: /*#__PURE__*/_jsx(ProductName, {
          apiName: ownedProductTier
        })
      }
    });
  }
  // this is last because a trial can still be available if it's been long enough since activation
  // however, if the other two conditions are false *and* it's unavailable then it's definitely this one
  if (highestTrialStatus === TRIAL_STATUS.TRIAL_EXPIRED) {
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "trial-components-library.picker.tooltips.alreadyTrialed",
      "data-test-id": "picker-tooltip-alreadyTrialed",
      options: {
        productName: /*#__PURE__*/_jsx(ProductName, {
          apiName: apiName
        })
      }
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    "data-test-id": "picker-tooltip-notAvailable",
    message: "trial-components-library.picker.tooltips.notAvailable"
  });
};

/**
 * Get remaining days in a trial
 */
export const getDaysInTrialRemaining = timestamp => {
  const hoursRemaining = I18n.moment(timestamp).diff(I18n.moment(), 'hours');
  return hoursRemaining < 24 && hoursRemaining > 0 ? 1 : I18n.moment(timestamp).diff(I18n.moment(), 'days');
};
const getIsPortalCreatedAWeekAgo = (trialStartDate, portalCreationDate) => {
  const portalCreatedAtMoment = I18n.moment(portalCreationDate);
  const trialStartDateMoment = I18n.moment(trialStartDate);
  // Note: This may need to change a fetch to the experiment date
  const daysSincePortalCreated = trialStartDateMoment.diff(portalCreatedAtMoment, 'days');
  // We want a portal that was create 0 - 7 days ago
  return daysSincePortalCreated < 8;
};
export const useIsEligibleForHC0133 = ({
  scopes
}) => {
  const langCode = getLang();
  const isPortalCreatedAWeekAgo = useRef(false);
  const isPortalDateRequested = useRef(false);
  const [isPortalDateLoading, setIsPortalDateLoading] = useState(false);
  useEffect(() => {
    const fetchPortalCreationDate = async () => {
      setIsPortalDateLoading(true);
      isPortalDateRequested.current = true;
      try {
        const [trialBannerStatus, createdAt] = await Promise.all([fetchTrialBannerStatus(), fetchHubCreationDate()]);
        const trialStartDate = (trialBannerStatus === null || trialBannerStatus === void 0 ? void 0 : trialBannerStatus.createdAt) || I18n.moment().add(10, 'days').valueOf();
        isPortalCreatedAWeekAgo.current = getIsPortalCreatedAWeekAgo(trialStartDate, createdAt);
      } catch (error) {
        // Default to not eligible if we can't determine creation date
        isPortalCreatedAWeekAgo.current = false;
      } finally {
        setIsPortalDateLoading(false);
      }
    };
    if (!isPortalDateRequested.current && !isPortalDateLoading) {
      void fetchPortalCreationDate();
    }
  }, [isPortalDateRequested, isPortalDateLoading]);
  return useMemo(() => {
    const isFreePortal = getIsFreePortal(scopes);
    const hasActivatedProTrial = hasActiveProTrial(scopes);
    return {
      isEligible: isFreePortal && hasActivatedProTrial && isPortalCreatedAWeekAgo.current && langCode === 'en',
      isLoading: isPortalDateLoading
    };
  }, [scopes, langCode, isPortalCreatedAWeekAgo, isPortalDateLoading]);
};